import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Filter, Info, ChevronRight, CalendarDays } from 'lucide-react';
import { useFinanceStore } from '../store/useFinanceStore';
import TransactionItem from '../components/TransactionItem';
import StatCard from '../components/StatCard';
import NewTransactionModal from '../components/NewTransactionModal';
import { Wallet, ArrowUp, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { format } from 'date-fns';
import CardManager from '../components/CardManager';

const Transacoes = () => {
  const { transactions, balance, monthlyIncome, monthlyExpenses } = useFinanceStore();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const loadStoredFilters = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const raw = localStorage.getItem('transacoesFilters');
      if (!raw) return null;
      return JSON.parse(raw) as {
        searchTerm?: string;
        filterCategory?: string;
        rangeType?: 'last7' | 'day' | 'week' | 'fortnight' | 'month';
        specificDay?: string;
        weekStart?: string;
        fortnightStart?: string;
        monthValue?: string;
      };
    } catch (error) {
      console.warn('Falha ao carregar filtros salvos', error);
      return null;
    }
  };

  const storedFilters = loadStoredFilters();

  const [searchTerm, setSearchTerm] = useState(storedFilters?.searchTerm ?? '');
  const [filterCategory, setFilterCategory] = useState(storedFilters?.filterCategory ?? 'all');
  const [rangeType, setRangeType] = useState<'last7' | 'day' | 'week' | 'fortnight' | 'month'>(
    storedFilters?.rangeType ?? 'last7'
  );
  const [specificDay, setSpecificDay] = useState(storedFilters?.specificDay ?? '');
  const [weekStart, setWeekStart] = useState(storedFilters?.weekStart ?? '');
  const [fortnightStart, setFortnightStart] = useState(storedFilters?.fortnightStart ?? '');
  const [monthValue, setMonthValue] = useState(storedFilters?.monthValue ?? '');
  const [isNewTransactionModalOpen, setIsNewTransactionModalOpen] = useState(false);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const parseDate = (value: string) => {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const payload = {
      searchTerm,
      filterCategory,
      rangeType,
      specificDay,
      weekStart,
      fortnightStart,
      monthValue,
    };

    localStorage.setItem('transacoesFilters', JSON.stringify(payload));
  }, [filterCategory, fortnightStart, monthValue, rangeType, searchTerm, specificDay, weekStart]);

  const { startDate, endDate, rangeLabel } = useMemo(() => {
    const now = new Date();

    if (rangeType === 'day') {
      const date = parseDate(specificDay) ?? now;
      const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59, 59, 999);
      return { startDate: start, endDate: end, rangeLabel: start.toLocaleDateString('pt-BR') };
    }

    if (rangeType === 'week') {
      const chosen = parseDate(weekStart) ?? now;
      const start = new Date(chosen.getFullYear(), chosen.getMonth(), chosen.getDate());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {
        startDate: start,
        endDate: end,
        rangeLabel: `${start.toLocaleDateString('pt-BR')} – ${end.toLocaleDateString('pt-BR')}`
      };
    }

    if (rangeType === 'fortnight') {
      const chosen = parseDate(fortnightStart) ?? now;
      const start = new Date(chosen.getFullYear(), chosen.getMonth(), chosen.getDate());
      const end = new Date(start);
      end.setDate(start.getDate() + 13);
      return {
        startDate: start,
        endDate: end,
        rangeLabel: `${start.toLocaleDateString('pt-BR')} – ${end.toLocaleDateString('pt-BR')}`
      };
    }

    if (rangeType === 'month') {
      const chosen = monthValue ? parseDate(`${monthValue}-01`) ?? now : now;
      const start = new Date(chosen.getFullYear(), chosen.getMonth(), 1);
      const end = new Date(chosen.getFullYear(), chosen.getMonth() + 1, 0, 23, 59, 59, 999);
      return {
        startDate: start,
        endDate: end,
        rangeLabel: start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
      };
    }

    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return {
      startDate: start,
      endDate: end,
      rangeLabel: `Últimos 7 dias (${start.toLocaleDateString('pt-BR')} – ${end.toLocaleDateString('pt-BR')})`
    };
  }, [fortnightStart, monthValue, rangeType, specificDay, weekStart]);

  useEffect(() => {
    setIsDetailsExpanded(false);
  }, [rangeLabel]);

  const dateFilteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const txDate = new Date(transaction.date);
      if (Number.isNaN(txDate.getTime())) return false;
      return txDate >= startDate && txDate <= endDate;
    });
  }, [endDate, startDate, transactions]);

  const categoryOptions = useMemo(() => {
    const counts = new Map<string, number>();

    dateFilteredTransactions.forEach((transaction) => {
      const label = transaction.customCategoryName?.trim() || transaction.category;
      if (!label) return;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [dateFilteredTransactions]);

  const filteredTransactions = useMemo(() => {
    return dateFilteredTransactions.filter((transaction) => {
      const label = transaction.customCategoryName?.trim() || transaction.category;
      const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || label === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [dateFilteredTransactions, searchTerm, filterCategory]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        {/* Header com título em bloco responsivo ao tema */}
        <div
          className={`rounded-xl p-6 shadow-lg transition-colors ${
            isDark
              ? 'bg-slate-800'
              : 'bg-gradient-to-r from-white via-teal-50 to-cyan-50 border border-teal-200 shadow-teal-100/40'
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Transações
            </h2>
            <button 
              onClick={() => setIsNewTransactionModalOpen(true)}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors justify-center sm:justify-start ${
                isDark
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md shadow-emerald-200/40 hover:from-teal-500 hover:to-emerald-600'
              }`}
            >
              <Plus size={16} />
              Nova Transação
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            title="Saldo Total"
            value={`R$${balance.toLocaleString('pt-BR')}`}
            icon={Wallet}
            bgColor="bg-blue-900"
            borderColor="border-blue-500"
            lightBgColor="bg-gradient-to-br from-white via-teal-50 to-cyan-50"
            lightBorderColor="border-teal-200"
            lightTitleColor="text-slate-600"
            lightValueColor="text-cyan-700"
            lightSubtitleColor="text-slate-500"
            lightIconWrapper="p-3 bg-cyan-100 rounded-full"
            lightIconColor="text-cyan-600"
          />
          <StatCard
            title="Receitas do Mês"
            value={`R$${monthlyIncome.toLocaleString('pt-BR')}`}
            icon={ArrowUp}
            bgColor="bg-green-900"
            borderColor="border-green-500"
            lightBgColor="bg-gradient-to-br from-white via-emerald-50 to-emerald-100"
            lightBorderColor="border-emerald-200"
            lightTitleColor="text-emerald-700"
            lightValueColor="text-emerald-700"
            lightSubtitleColor="text-emerald-600"
            lightIconWrapper="p-3 bg-emerald-100 rounded-full"
            lightIconColor="text-emerald-600"
          />
          <StatCard
            title="Saídas do Mês"
            value={`R$${monthlyExpenses.toLocaleString('pt-BR')}`}
            icon={ArrowDown}
            bgColor="bg-red-900"
            borderColor="border-red-500"
            lightBgColor="bg-gradient-to-br from-white via-rose-50 to-rose-100"
            lightBorderColor="border-rose-200"
            lightTitleColor="text-rose-600"
            lightValueColor="text-rose-600"
            lightSubtitleColor="text-rose-500"
            lightIconWrapper="p-3 bg-rose-100 rounded-full"
            lightIconColor="text-rose-600"
          />
        </div>

        {/* Todas as transações - recolhido por padrão */}
        <div
          className={`rounded-lg ${isDark ? 'bg-slate-800' : 'bg-white border border-teal-200 shadow-sm shadow-teal-100/30'}`}
        >
          <button
            type="button"
            onClick={() => {
              setIsAllExpanded((prev) => {
                const next = !prev;
                if (!next) {
                  setIsDetailsExpanded(false);
                }
                return next;
              });
            }}
            className="w-full px-5 py-4 flex items-center justify-between gap-3"
          >
            <div className="text-left">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Todas as transações
              </h3>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {isAllExpanded
                  ? `Exibindo registros de ${rangeLabel}`
                  : 'Toque para ver e filtrar lançamentos recentes'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {filteredTransactions.length} registros
              </span>
              <ChevronRight
                size={18}
                className={`transition-transform ${isAllExpanded ? 'rotate-90' : ''} ${
                  isDark ? 'text-slate-300' : 'text-slate-500'
                }`}
              />
            </div>
          </button>

          <AnimatePresence initial={false}>
            {isAllExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="px-4 pb-5 space-y-5"
              >
                {/* Filters */}
                <div
                  className={`rounded-lg p-4 transition-colors ${
                    isDark
                      ? 'bg-slate-900/60 border border-slate-700'
                      : 'bg-white border border-teal-200 shadow-sm shadow-teal-100/30'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search
                        className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                          isDark ? 'text-gray-400' : 'text-teal-500'
                        }`}
                        size={20}
                      />
                      <input
                        type="text"
                        placeholder="Buscar transações..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none transition-colors ${
                          isDark
                            ? 'bg-slate-700 border border-slate-600 text-white placeholder-gray-400 focus:border-emerald-500'
                            : 'bg-teal-50 border border-teal-200 text-slate-700 placeholder-teal-500/60 focus:border-teal-400'
                        }`}
                      />
                    </div>
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
                      <div className="flex items-center gap-2">
                        <Filter className={isDark ? 'text-gray-400' : 'text-teal-500'} size={20} />
                        <select
                          value={filterCategory}
                          onChange={(e) => setFilterCategory(e.target.value)}
                          className={`rounded-lg px-3 py-2 focus:outline-none transition-colors ${
                            isDark
                              ? 'bg-slate-700 border border-slate-600 text-white focus:border-emerald-500'
                              : 'bg-white border border-teal-200 text-slate-700 focus:border-teal-400'
                          }`}
                        >
                          <option value="all">Todas as categorias</option>
                          {categoryOptions.map((category) => (
                            <option key={category.label} value={category.label}>
                              {category.label} ({category.count})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-2">
                          <CalendarDays className={isDark ? 'text-gray-400' : 'text-teal-500'} size={20} />
                          <select
                            value={rangeType}
                            onChange={(e) => setRangeType(e.target.value as typeof rangeType)}
                            className={`rounded-lg px-3 py-2 focus:outline-none transition-colors ${
                              isDark
                                ? 'bg-slate-700 border border-slate-600 text-white focus:border-emerald-500'
                                : 'bg-white border border-teal-200 text-slate-700 focus:border-teal-400'
                            }`}
                          >
                            <option value="last7">Últimos 7 dias</option>
                            <option value="day">Dia específico</option>
                            <option value="week">Semana</option>
                            <option value="fortnight">Quinzena</option>
                            <option value="month">Mês</option>
                          </select>
                        </div>

                        {rangeType === 'day' && (
                          <input
                            type="date"
                            value={specificDay}
                            onChange={(e) => setSpecificDay(e.target.value)}
                            className={`w-full sm:w-auto rounded-lg px-3 py-2 text-sm focus:outline-none ${
                              isDark ? 'bg-slate-700 border border-slate-600 text-white' : 'bg-white border border-teal-200'
                            }`}
                            max={today.toISOString().split('T')[0]}
                          />
                        )}

                        {rangeType === 'week' && (
                          <input
                            type="date"
                            value={weekStart}
                            onChange={(e) => setWeekStart(e.target.value)}
                            className={`w-full sm:w-auto rounded-lg px-3 py-2 text-sm focus:outline-none ${
                              isDark ? 'bg-slate-700 border border-slate-600 text-white' : 'bg-white border border-teal-200'
                            }`}
                            max={today.toISOString().split('T')[0]}
                          />
                        )}

                        {rangeType === 'fortnight' && (
                          <input
                            type="date"
                            value={fortnightStart}
                            onChange={(e) => setFortnightStart(e.target.value)}
                            className={`w-full sm:w-auto rounded-lg px-3 py-2 text-sm focus:outline-none ${
                              isDark ? 'bg-slate-700 border border-slate-600 text-white' : 'bg-white border border-teal-200'
                            }`}
                            max={today.toISOString().split('T')[0]}
                          />
                        )}

                        {rangeType === 'month' && (
                          <input
                            type="month"
                            value={monthValue}
                            onChange={(e) => setMonthValue(e.target.value)}
                            className={`w-full sm:w-auto rounded-lg px-3 py-2 text-sm focus:outline-none ${
                              isDark ? 'bg-slate-700 border border-slate-600 text-white' : 'bg-white border border-teal-200'
                            }`}
                            max={`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Transactions List */}
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Info className={isDark ? 'text-gray-300' : 'text-teal-500'} size={16} />
                        <span className={isDark ? 'text-gray-300' : 'text-slate-600'}>
                          Exibindo {filteredTransactions.length} transações ({rangeLabel})
                        </span>
                      </div>

                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        {categoryOptions.length} categorias no período
                      </span>
                    </div>

                    <div
                      className={`rounded-lg divide-y ${
                        isDark
                          ? 'divide-slate-700 bg-slate-900/60 border border-slate-700'
                          : 'divide-gray-100 bg-white shadow-sm'
                      }`}
                    >
                      {filteredTransactions.length > 0 ? (
                        filteredTransactions.map((transaction) => (
                          <TransactionItem
                            key={`${transaction.id}-${rangeLabel}`}
                            transaction={transaction}
                          />
                        ))
                      ) : (
                        <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          Nenhuma transação encontrada
                        </p>
                      )}
                    </div>

                    <div
                      className={`rounded-lg ${
                        isDark ? 'bg-slate-900/60 border border-slate-700' : 'bg-white border border-teal-200'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setIsDetailsExpanded((prev) => !prev)}
                        className="w-full px-5 py-4 flex items-center justify-between gap-4"
                      >
                        <div>
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Dados detalhados
                          </h3>
                          <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                            💡 Clique para expandir e visualizar todas as transações do período
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {filteredTransactions.length} registros
                          </span>
                          <ChevronRight
                            size={18}
                            className={`transition-transform ${isDetailsExpanded ? 'rotate-90' : ''} ${
                              isDark ? 'text-slate-300' : 'text-slate-500'
                            }`}
                          />
                        </div>
                      </button>

                      <AnimatePresence initial={false}>
                        {isDetailsExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="px-5 pb-5"
                          >
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                                    <th className={`text-left font-medium py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      Data
                                    </th>
                                    <th className={`text-left font-medium py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      Descrição
                                    </th>
                                    <th className={`text-left font-medium py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      Categoria
                                    </th>
                                    <th className={`text-right font-medium py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      Valor
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredTransactions.map((item, index) => (
                                    <tr
                                      key={item.id ?? index}
                                      className={`border-b transition-colors ${
                                        isDark
                                          ? 'border-slate-700 hover:bg-slate-800/60'
                                          : 'border-gray-100 hover:bg-teal-50'
                                      }`}
                                    >
                                      <td className={`py-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {format(new Date(item.date), 'dd/MM/yyyy')}
                                      </td>
                                      <td className={`py-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {item.description}
                                      </td>
                                      <td className={`py-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {item.customCategoryName?.trim() || item.category}
                                      </td>
                                      <td
                                        className={`py-3 text-right font-semibold ${
                                          item.amount >= 0
                                            ? isDark
                                              ? 'text-emerald-400'
                                              : 'text-emerald-600'
                                            : isDark
                                              ? 'text-red-400'
                                              : 'text-red-600'
                                        }`}
                                      >
                                        {item.amount >= 0 ? '+' : '-'}
                                        {Math.abs(item.amount).toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL'
                                        })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <CardManager />
      </motion.div>

      <NewTransactionModal
        isOpen={isNewTransactionModalOpen}
        onClose={() => setIsNewTransactionModalOpen(false)}
      />
    </>
  );
};

export default Transacoes;